import { getClearedValue } from '@yme/utils/common';
import { checkMarineEngineProductType } from '@yme/utils/productTypes';
import { STATUS_COLOR_CHANGE, STATUS_INIT, STATUS_RELOAD } from '../constants';
function getSplittedUpValue(value) {
    const startIndex = value.indexOf('(');
    const endIndex = value.indexOf(')');
    const isSplittedUp = startIndex >= 0 && endIndex >= 0;
    return {
        titlePrefix: isSplittedUp ? value.slice(startIndex + 1, endIndex) : undefined,
        specialValue: isSplittedUp ? value.slice(0, startIndex).trim() : undefined,
    };
}
export function buildShaftLengthList(options) {
    const res = options.reduce((acc = [], itemValue) => {
        const { titlePrefix, specialValue } = getSplittedUpValue(itemValue);
        const itemOptions = {
            value: itemValue,
            titlePrefix,
            specialValue,
        };
        if (!acc.length) {
            return [itemOptions];
        }
        return [...acc, itemOptions];
    }, []);
    return res;
}
export function getShaftLengthValue(listOptions, currentValue) {
    return listOptions?.find((item) => item.value === currentValue);
}
export function getRotationValues(listOptions) {
    return listOptions?.reduce((acc, item) => {
        const newItem = {
            key: item,
            value: item,
        };
        return !acc ? [newItem] : [...acc, newItem];
    }, []);
}
export function getPossibleShaftLengthOptions(variants, rotation) {
    if (variants) {
        const result = variants?.reduce((acc, item) => {
            const constrain = rotation ? item.rotation === rotation : item;
            if (!acc && item.shaftLength && constrain) {
                return [item.shaftLength];
            }
            const isUniqueShaftLength = acc.length > 0 && acc.some((j) => j === item.shaftLength);
            if (constrain && !isUniqueShaftLength && item.shaftLength) {
                return [...acc, item.shaftLength];
            }
            return [...acc];
        }, []);
        return result;
    }
    return [];
}
function initialMarineEnginesFilterOptions({ variantsOfProduct, }) {
    const activeVariant = variantsOfProduct.find((item) => {
        const res = [
            item.master,
            item.rotation,
            item.shaftLength,
            item.startingMethod,
            item.controlMethod,
        ].every((i) => i);
        return res;
    }) ||
        variantsOfProduct.find((item) => {
            const res = [
                item.master,
                item.rotation,
                item.shaftLength,
                item.startingMethod || item.controlMethod,
            ].every((i) => i);
            return res;
        }) ||
        variantsOfProduct.find((item) => {
            const res = [
                item.rotation,
                item.shaftLength,
                item.startingMethod,
                item.controlMethod,
                item.stock > 0,
                item.prices.amount > 0,
            ].every((i) => i);
            return res;
        }) ||
        variantsOfProduct.find((item) => {
            const res = [
                item.rotation,
                item.shaftLength,
                item.startingMethod,
                item.controlMethod,
                item.prices.amount > 0,
            ].every((i) => i);
            return res;
        }) ||
        variantsOfProduct.find((item) => {
            const res = [
                // find almost any result
                item.rotation,
                item.shaftLength,
                item.prices.amount > 0,
            ].every((i) => i);
            return res;
        });
    return activeVariant;
}
function reloadMarineEnginesFilterOptions({ sku, variantsOfProduct, }) {
    const activeVariant = variantsOfProduct.find((item) => [item.sku, item.sku === sku].every((i) => i)) ||
        variantsOfProduct.find((item) => [item.sku, item.rotation, item.shaftLength, item.stock > 0].every((i) => i));
    return activeVariant;
}
function changeColorMarineEnginesFilterOptions({ variantsOfProduct, rotation, shaftLength, startingMethod, controlMethod, }) {
    const activeVariant = variantsOfProduct.find((item) => {
        const res = [
            item.rotation === rotation,
            item.shaftLength === shaftLength,
            item.startingMethod === startingMethod,
            item.controlMethod === controlMethod,
        ].every((i) => i);
        return res;
    }) ||
        variantsOfProduct.find((item) => {
            const res = [
                item.rotation === rotation,
                item.shaftLength === shaftLength,
                item.stock > 0,
            ].every((i) => i);
            return res;
        }) ||
        variantsOfProduct.find((item) => {
            const res = [
                item.rotation === rotation,
                item.shaftLength === shaftLength,
                item.prices.amount > 0,
            ].every((i) => i);
            return res;
        }) ||
        variantsOfProduct.find((item) => {
            const res = [
                // find option (rotations and shaftLengths values should be equal)
                item.rotation === rotation,
                item.shaftLength === shaftLength,
            ].every((i) => i);
            return res;
        }) ||
        variantsOfProduct.find((item) => {
            const res = [
                // find option (rotations value should be equal and diff shaftLength)
                item.rotation === rotation,
                item.shaftLength !== shaftLength,
                item.prices.amount > 0,
            ].every((i) => i);
            return res;
        }) ||
        variantsOfProduct.find((item) => {
            const res = [
                // find almost any result (rotations value should be equal)
                item.rotation === rotation,
                item.shaftLength !== shaftLength,
            ].every((i) => i);
            return res;
        });
    return activeVariant;
}
// Marine Engines Filter
export function prepareMarineEnginesFilterOptions({ colorVariants, index = 0, rotation = '', shaftLength = '', sku = '', startingMethod = '', controlMethod = '', statusType, }) {
    const currentProductType = colorVariants[index].productType
        .toUpperCase()
        .trim()
        .replace(' ', '')
        .replace('-', '');
    const commonProps = {
        colorItemIndex: index,
        colourId: colorVariants[index].colourId,
        productType: currentProductType,
    };
    const variantsOfProduct = colorVariants[index]?.variantsOfProduct;
    const isCurrentMarineEngine = Boolean(variantsOfProduct && checkMarineEngineProductType(currentProductType));
    if (isCurrentMarineEngine) {
        let activeVariant;
        if (statusType === STATUS_INIT && !sku && !rotation) {
            activeVariant = initialMarineEnginesFilterOptions({
                variantsOfProduct,
            });
        }
        if (statusType === STATUS_RELOAD && sku && !rotation) {
            activeVariant = reloadMarineEnginesFilterOptions({
                variantsOfProduct,
                sku,
            });
        }
        if (statusType === STATUS_COLOR_CHANGE) {
            activeVariant = changeColorMarineEnginesFilterOptions({
                variantsOfProduct,
                rotation,
                shaftLength,
                startingMethod,
                controlMethod,
            });
        }
        // Default/common cases - Show any variant by size in current ColorGroup (first result with compared condition)
        activeVariant =
            activeVariant ||
                variantsOfProduct.find((item) => item.stock > 0 && item.prices.amount > 0) ||
                variantsOfProduct.find((item) => item.prices.amount > 0) ||
                variantsOfProduct.find((item) => Boolean(item.rotation));
        return {
            ...commonProps,
            sku: activeVariant?.sku || '',
            rotation: activeVariant?.rotation || '',
            shaftLength: activeVariant?.shaftLength || '',
            startingMethod: activeVariant?.startingMethod || '',
            controlMethod: activeVariant?.controlMethod || '',
        };
    }
    return {
        ...commonProps,
        sku: '',
        rotation: '',
        shaftLength: '',
        startingMethod: '',
        controlMethod: '',
    };
}
export function getAvailableSkuByRotationValue({ colorVariants, rotationValue, shaftLength, }) {
    const exactCase = colorVariants?.find((item) => item.rotation === rotationValue && item.shaftLength === shaftLength && item.stock > 0);
    const prettySuitableCase = colorVariants?.find((item) => item.rotation === rotationValue && item.shaftLength === shaftLength);
    const alternativeCase = colorVariants?.find((item) => item.rotation === rotationValue);
    return exactCase || prettySuitableCase || alternativeCase;
}
export const getAvailableSkuByShaftLengthValue = ({ colorVariants, shaftLengthValue = '', rotationStateValue = '', }) => {
    const exactCase = colorVariants?.find((item) => item.rotation === rotationStateValue &&
        item.shaftLength === shaftLengthValue &&
        item.stock > 0 &&
        item.prices.amount > 0);
    const prettySuitableCase = colorVariants?.find((item) => item.rotation === rotationStateValue &&
        item.shaftLength === shaftLengthValue &&
        item.prices.amount > 0);
    const alternativeCase = colorVariants?.find((item) => item.rotation === rotationStateValue && item.shaftLength === shaftLengthValue);
    return exactCase || prettySuitableCase || alternativeCase;
};
export const getAvailableSkuByStartingMethodValue = ({ colorVariants, startingMethodValue, rotation = '', shaftLength = '', }) => {
    const exactCase = colorVariants?.find((item) => item.rotation === rotation &&
        item.shaftLength === shaftLength &&
        item.startingMethod === startingMethodValue &&
        item.stock > 0 &&
        item.prices.amount > 0);
    const prettySuitableCase = colorVariants?.find((item) => item.rotation === rotation &&
        item.shaftLength === shaftLength &&
        item.startingMethod === startingMethodValue &&
        item.prices.amount > 0);
    const alternativeCase = colorVariants?.find((item) => item.rotation === rotation &&
        item.shaftLength === shaftLength &&
        item.startingMethod === startingMethodValue);
    return exactCase || prettySuitableCase || alternativeCase;
};
export const getAvailableSkuByControlMethodValue = ({ colorVariants, controlMethodValue, rotation, shaftLength, }) => {
    const exactCase = colorVariants?.find((item) => item.rotation === rotation &&
        item.shaftLength === shaftLength &&
        item.controlMethod === controlMethodValue &&
        item.prices.amount > 0 &&
        item.stock > 0);
    const prettySuitableCase = colorVariants?.find((item) => item.rotation === rotation &&
        item.shaftLength === shaftLength &&
        item.controlMethod === controlMethodValue &&
        item.prices.amount > 0);
    const alternativeCase = colorVariants?.find((item) => item.rotation === rotation &&
        item.shaftLength === shaftLength &&
        item.controlMethod === controlMethodValue);
    return exactCase || prettySuitableCase || alternativeCase;
};
export function buildCommonOptions({ allOptions, possibleOptions, }) {
    const result = allOptions.map((item) => ({
        value: getClearedValue(item),
        label: item,
        isDisabled: !possibleOptions.some((i) => i === item),
    }));
    return result.filter((item) => Boolean(item.value.trim().length));
}
